import { useCallback, useEffect, useState } from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import i18n from "@/i18n";
import { DateTime } from "luxon";

export interface PartialDateParts {
  day?: number;
  month?: number;
  year?: number;
}
export const useScrollHandler = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const mainLayout = document.getElementById("mainLayout");

  const handleScroll = useCallback(() => {
    const position = mainLayout?.scrollTop;
    setScrollPosition(position ?? 0);
  }, [mainLayout]);

  useEffect(() => {
    mainLayout?.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      mainLayout?.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, mainLayout]);

  return scrollPosition;
};
export const useIsTablet = () => {
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1023);

  useEffect(() => {
    const handleResize = () => setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1023);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isTablet;
};

export const useIsNotDesktop = () => {
  const [isNotDesktop, setIsNotDesktop] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsNotDesktop(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isNotDesktop;
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent;

  // Check for common mobile and tablet user agent strings
  if (/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    // Check if it's a tablet specifically (e.g., iPad, Android tablet)
    if (/iPad|Android|Tablet/i.test(userAgent) && !/Mobile/i.test(userAgent)) {
      return "Tablet";
    }
    return "Mobile";
  } else {
    return "Desktop";
  }
};

export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(getDeviceType() === "Desktop");

  useEffect(() => {
    const handleResize = () => {
      // Only update if the device type actually changes
      const newDeviceType = getDeviceType();
      setIsDesktop(newDeviceType === "Desktop");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isDesktop;
};

export const navigateTo = (path: string, queryParams?: Record<string, string>) => {
  const routeEvent = new CustomEvent("RouteEvent", { detail: { path, queryParams } });
  window.dispatchEvent(routeEvent);
};

export const toLocalNumber = (number: number | string, withDecimal = true) => {
  return number
    ? String(
        Number(number).toLocaleString("de-DE", {
          minimumFractionDigits: withDecimal ? 2 : 0,
          maximumFractionDigits: 2,
        })
      )
    : "";
};

export const hasBeenReached = (elementId: string, scrollPosition: number, offset: number) => {
  const mainLayout = document.getElementById("mainLayout");
  const element = document.getElementById(elementId);
  if (!element) return false;
  const amountOfPageScroll = mainLayout?.scrollTop;
  let elementPositionY = 0;
  if (mainLayout !== null) {
    elementPositionY = element.getBoundingClientRect().top + mainLayout.scrollTop;
  }
  const status = amountOfPageScroll ?? 0 + offset > elementPositionY ? true : false;
  return status;
};

export const isEmail = (value?: string) => {
  return typeof value !== "string" || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i.test(value)
    ? undefined
    : i18n.t("validation.isEmail");
};

export function isPhoneNumberValid(value: string) {
  if (value && typeof value === "string") {
    if (value.length < 6) return i18n.t("validation.phoneMinAmount");
    try {
      return isValidPhoneNumber(value, "DE") === true ? undefined : i18n.t("validation.isPhoneValid");
    } catch (error) {
      return i18n.t("validation.isPhoneValid");
    }
  }
  return undefined;
}

export const required = (value?: string) => (value ? undefined : i18n.t("validation.required"));

export function stripWhitespaces(input: string): string {
  return (
    input
      .replace(/\n/g, "")
      // .replace(/[\t ]+</g, "<")
      .replace(/>[\t ]+</g, "><")
      .replace(/>[\t ]+$/g, ">")
  );
}

// eslint-disable-next-line
export function fromDateParts({ day = 0, month = 0, year = 0 }: PartialDateParts = {}): Date | any {
  const date = DateTime.fromObject({ day, month, year });

  if (date.isValid) {
    return date.toJSDate();
  }
}
export function getDateParts(date?: Date | string, parseFormat?: string) {
  if (date instanceof Date) {
    const parsedDate = DateTime.fromJSDate(date);
    if (parsedDate.isValid) {
      return {
        day: parsedDate.day,
        month: parsedDate.month,
        year: parsedDate.year,
      };
    }
  } else if (typeof date === "string") {
    const parsed = DateTime.fromFormat(date, parseFormat ?? "");

    if (parsed.isValid) {
      return {
        day: parsed.day,
        month: parsed.month,
        year: parsed.year,
      };
    }
  }
}

export function htmlListToArray(text?: string): Array<string> {
  if (typeof text === "string") {
    const matches = text.replace(/<li>/g, "\r<li>");

    return htmlList(matches);
  } else {
    return [];
  }
}
const htmlList = (text: string) => {
  if (typeof text === "string") {
    const matches: RegExpMatchArray | null = text.match(/(?:<li>(.*)?<\/li>)/g);
    if (matches !== null && matches !== undefined && matches.length > 0) {
      return matches.map((match) => {
        const s = /(?:<li>(.*)?<\/li>)/g.exec(match);
        if (s && s.length > 1) {
          return s[1];
        } else {
          return "";
        }
      });
    } else {
      return [];
    }
  } else {
    return [];
  }
};

export const formatForBackend = (date?: Date | string, parseFormat?: string) => {
  if (!date || (date instanceof Date && date.getFullYear() === 0)) {
    return "";
  }

  let parsedDate;
  if (typeof date === "string") {
    parsedDate = DateTime.fromFormat(date, parseFormat ?? "");
  } else {
    parsedDate = DateTime.fromJSDate(date);
  }

  if (!parsedDate.isValid) {
    parsedDate = DateTime.fromFormat(date as string, parseFormat ?? "");
    if (!parsedDate.isValid) {
      throw Error(
        `Invalid date format for ${date && date.toString()}. Expected format ${parseFormat} or "{backendDateFormat}`
      );
    }
  }

  return parsedDate.toFormat(parseFormat ?? "");
};
